<template>
  <div class="edit-video-course">
    <div class="edit-video-course__body container">
      <div class="row">
        <div class="col-12">
          <div class="edit-video-course__header-title typography--color-primary text-center">
            <span v-if="course">
              {{ $t('publishers.modal.edit_video_course.title') }}
            </span>
            <span v-else>
              {{ $t('publishers.modal.new_video_course.title') }}
            </span>
          </div>

          <div class="edit-video-course__form">
            <div class="form-group">
              <label for="subject-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.subject') }}
              </label>

              <select v-model="subjectId" id="subject-select" class="form-control" @change="onSubjectSelect">
                <option disabled value="">
                  {{ $t('publishers.modal.new_series.subject_placeholder') }}
                </option>
                <option v-for="sjId in subjectIds.filter(id => id !== '0' && id !== '9999')"
                        :key="sjId"
                        :value="sjId">
                  {{ getTranslatedName(subjectMap[sjId]) }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="grade-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.grade') }}
              </label>

              <select v-model="gradeId" id="grade-select" class="form-control" @change="onGradeSelect">
                <option disabled value="">
                  {{ $t('publishers.modal.new_series.grade_placeholder') }}
                </option>
                <option v-for="gId in gradeIds.filter(id => id !== '0' && id !== '9999')"
                        :key="gId"
                        :value="gId">
                  {{ getTranslatedName(gradeMap[gId]) }}
                </option>
              </select>
            </div>

            <!-- core concept select -->
            <div class="form-group" v-if="isCoreConcepts">
              <label
                for="core-concept-select"
                class="typography--body-3 typography--color-secondary typography--font-weight-600"
              >
                {{ $t('card_details.card_meta.core_concept') }}
              </label>

              <select
                v-model="coreConceptId"
                id="core-concept-select"
                class="form-control"
              >
                <option disabled value="">
                  {{ $t('card_details.card_meta.core_concepts_placeholder') }}
                </option>
                <option v-for="coreConcept in coreConcepts" :key="coreConcept.id" :value="coreConcept.id">
                  [{{ coreConcept.id }}] {{ coreConcept.name }} | {{ coreConcept.code }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="video-course-title-input"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.name') }}
              </label>

              <input
                type="text"
                id="video-course-title-input"
                class="form-control"
                maxlength="40"
                :placeholder="$t('publishers.modal.new_series.name_placeholder')"
                v-model.trim="courseTitle"
              >
            </div>

            <div class="form-group">
              <label for="video-course-description-input"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('publishers.modal.new_series.description') }}
              </label>

              <textarea
                id="video-course-description-input"
                class="form-control"
                rows="5"
                maxlength="500"
                :placeholder="$t('publishers.modal.new_series.description_placeholder')"
                v-model="courseDescription"
              />
            </div>

            <div class="text-center mt-2" v-if="course">
              <button class="btn btn-primary btn-full edit-video-course__create-btn"
                      :disabled="newCourseDisabled"
                      @click="update()">
                <span v-if="!busy">{{ $t('common.save') }}</span>
                <LoadingBounce v-else></LoadingBounce>
              </button>
            </div>

            <div class="text-center mt-2" v-else>
              <button class="btn btn-primary btn-full edit-video-course__create-btn"
                      :disabled="newCourseDisabled"
                      @click="create()">
                <span v-if="!busy">{{ $t('video_lectures.new_video_course.create') }}</span>
                <LoadingBounce v-else></LoadingBounce>
              </button>
            </div>

            <div class="text-center mt-2">
              <button class="btn btn-secondary btn-full edit-video-course__cancel-btn typography--color-secondary"
                      @click="prevLink()">
                {{ $t('btn.redirect_to_root') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, isEmpty, head } from 'lodash';
  import publisherOnly from '@/mixins/publisherOnly';
  import labels from '@/mixins/labels';

  export default {
    name: 'edit-video-course',

    mixins: [
      publisherOnly,
      labels,
    ],

    data() {
      return {
        subjectId: '',
        gradeId: '',
        domainId: '',
        courseTitle: '',
        courseDescription: '',
        domainList: [],
        busy: false,
        coreConceptId: '',
        coreConcepts: [],
      };
    },

    computed: {
      ...mapGetters([
        'videoCourses',
        'subjectMap',
        'subjectIds',
        'gradeMap',
        'gradeIds',
        'coreConceptVideoCourse',
      ]),

      course() {
        return this.videoCourses[this.$route.params.course_id];
      },

      showDomainSelect() {
        return !isEmpty(this.domainList);
      },

      newCourseDisabled() {
        if (this.subjectId === '') {
          return true;
        }

        if (!isEmpty(this.domainList) && this.domainId === '') {
          return true;
        }

        if (isEmpty(this.courseTitle)) {
          return true;
        }

        return false;
      },

      isCoreConcepts() {
        return !isEmpty(this.coreConcepts);
      },

    },

    methods: {
      ...mapActions([
        'createNewVideoCourse',
        'updateVideoCourse',
        'fetchVideoCourse',
        'fetchCoreConcepts',
        'createCoreConceptVideoCourse',
        'fetchCoreConceptVideoCourses',
        'updateCoreConceptVideoCourse',
      ]),

      prevLink() {
        return this.$router.go(-1);
      },

      async onSubjectSelect() {
        const params = {
          grade_id: this.gradeId === 'null' ? null : this.gradeId,
          subject_id: this.subjectId,
        };
        this.coreConcepts = await this.fetchCoreConcepts(params);
      },

      async onGradeSelect() {
        const params = {
          grade_id: this.gradeId === 'null' ? null : this.gradeId,
          subject_id: this.subjectId,
        };
        this.coreConcepts = await this.fetchCoreConcepts(params);
      },

      create() {
        if (!this.busy) {
          this.busy = true;

          const params = {
            title: this.courseTitle,
            description: this.courseDescription || undefined,
            subject_id: this.subjectId || undefined,
            grade_id: this.gradeId === 'null' ? null : (this.gradeId || undefined),
            domain_id: this.domainId || undefined,
          };

          this.createNewVideoCourse(params)
            .then((videoCourses) => {
              if (this.isCoreConcepts && this.coreConceptId) {
                return this.createCoreConceptVideoCourse({
                  video_course_id: head(videoCourses).id,
                  core_concept_id: this.coreConceptId,
                });
              }

              return true;
            })
            .then(() => (
              this.$router.replace('/video-courses')
            ));
        }
      },

      update() {
        if (!this.busy) {
          this.busy = true;

          const params = {
            id: this.$route.params.course_id,
            title: this.courseTitle,
            description: this.courseDescription || undefined,
            subject_id: this.subjectId || undefined,
            grade_id: this.gradeId === 'null' ? null : (this.gradeId || undefined),
            domain_id: this.domainId || undefined,
          };

          this.updateVideoCourse(params)
            .then(() => {
              const data = {
                video_course_id: Number(this.$route.params.course_id),
                core_concept_id: this.coreConceptId,
              };

              if (
                !isEmpty(this.coreConceptVideoCourse)
                && this.coreConceptId
                && this.coreConceptId !== this.coreConceptVideoCourse.core_concept_id
              ) {
                data.id = this.coreConceptVideoCourse.id;
                return this.updateCoreConceptVideoCourse(data);
              }

              if (
                isEmpty(this.coreConceptVideoCourse)
                && this.coreConceptId
              ) {
                return this.createCoreConceptVideoCourse(data);
              }

              return true;
            })
            .then(() => {
              this.busy = false;
            });
        }
      },
    },

    beforeMount() {
      this.loading = true;

      const courseId = this.$route.params.course_id;

      if (isNil(courseId)) {
        this.loading = false;
        return true;
      }

      Promise.resolve()
        .then(() => {
          if (isEmpty(this.videoCourses[courseId])) {
            return this.fetchVideoCourse(courseId);
          }

          return this.videoCourses[courseId];
        })
        .then(() => {
          this.subjectId = this.course.subject_id || '';
          this.gradeId = this.course.grade_id;
          this.courseTitle = this.course.title;
          this.courseDescription = this.course.description || '';
        })
        .then(() => (
          this.fetchCoreConcepts({
            grade_id: this.gradeId === 'null' ? null : this.gradeId,
            subject_id: this.subjectId,
          })
        ))
        .then((coreConcepts) => {
          this.coreConcepts = coreConcepts;
          return true;
        })
        .then(() => (this.fetchCoreConceptVideoCourses({ video_course_id: courseId })))
        .then(() => {
          this.coreConceptId = this.coreConceptVideoCourse.core_concept_id;
          return true;
        })
        .then(() => {
          this.loading = false;
        });

      return true;
    },
  };
</script>

<style lang="scss" scoped>
  .edit-video-course {
    .edit-video-course__body {
      max-width: 400px;
      padding-bottom: 100px;
    }

    .edit-video-course__header-title {
      margin-top: 60px;
      font-size: 28px;
      line-height: 1.79;
    }

    .edit-video-course__form {
      margin-top: 40px;

      .form-group .form-control {
        font-size: 14px;
      }

      .form-group input.form-control {
        height: 47px;
      }
    }

    .edit-video-course__create-btn,
    .edit-video-course__cancel-btn {
      font-size: 16px;
      line-height: 1.88;
      font-weight: 400;
      padding-top: 5px;
      padding-bottom: 5px;
      border-radius: 4px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/video-courses/views/EditVideoCourse.vue